import { useCallback, useEffect, useState } from "react";
import { Banner, SectionHeading } from "../settings/settingsUi";
import { PersonalTotpClient } from "@/api/personalTotpClient";

const totpClient = new PersonalTotpClient();

export default function TotpSettingsSection() {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState(null);
  const [enroll, setEnroll] = useState(null);
  const [code, setCode] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [copied, setCopied] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await totpClient.getStatus();
      setEnabled(Boolean(res?.enabled));
    } catch (e) {
      setBanner({
        type: "err",
        text: e instanceof Error ? e.message : "Could not load authenticator status.",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  const startEnroll = async () => {
    setBusy(true);
    setBanner(null);
    try {
      const res = await totpClient.start();
      setEnroll({
        enroll_session: res.enroll_session,
        otpauth_uri: res.otpauth_uri,
        secret: res.secret,
        qr_svg: res.qr_svg || "",
      });
      setCode("");
    } catch (e) {
      setBanner({
        type: "err",
        text: e instanceof Error ? e.message : "Could not start authenticator setup.",
      });
    } finally {
      setBusy(false);
    }
  };

  const confirmEnroll = async () => {
    if (!enroll?.enroll_session || code.replace(/\s+/g, "").length < 6) return;
    setBusy(true);
    setBanner(null);
    try {
      await totpClient.confirm({
        enroll_session: enroll.enroll_session,
        code: code.replace(/\s+/g, ""),
      });
      setEnroll(null);
      setCode("");
      setEnabled(true);
      setBanner({ type: "ok", text: "Authenticator app is now enabled." });
    } catch (e) {
      setBanner({
        type: "err",
        text: e instanceof Error ? e.message : "Invalid authenticator code.",
      });
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    if (disableCode.replace(/\s+/g, "").length < 6) return;
    setBusy(true);
    setBanner(null);
    try {
      await totpClient.disable({ code: disableCode.replace(/\s+/g, "") });
      setDisableCode("");
      setEnabled(false);
      setBanner({ type: "ok", text: "Authenticator app is now off." });
    } catch (e) {
      setBanner({
        type: "err",
        text: e instanceof Error ? e.message : "Could not disable authenticator.",
      });
    } finally {
      setBusy(false);
    }
  };

  const copySecret = async () => {
    if (!enroll?.secret) return;
    try {
      await navigator.clipboard.writeText(enroll.secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="ui-card-static space-y-5 !p-6 sm:!p-8">
      <SectionHeading
        label="Two-factor authentication (2FA)"
        desc="Optional authenticator app (Google Authenticator, Authy, or similar). When enabled, you will enter a 6-digit code after password or Google sign-in."
      />

      {banner && <Banner type={banner.type} text={banner.text} onDismiss={() => setBanner(null)} />}

      {loading && (
        <p className="text-sm text-slate-500">Checking authenticator status…</p>
      )}

      {enabled ? (
        <div className="space-y-4">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Authenticator app is on. You will be asked for a 6-digit code at sign-in.
            If you lose the device, ask an admin to Reset 2FA on the Users page.
          </p>
          <div>
            <label htmlFor="totp-disable-code" className="mb-1.5 block text-sm font-semibold text-slate-800 dark:text-slate-200">
              Current authenticator code
            </label>
            <input
              id="totp-disable-code"
              className="ui-field max-w-xs"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={8}
              placeholder="6-digit code"
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value.replace(/[^\d]/g, "").slice(0, 6))}
              disabled={busy}
            />
          </div>
          <button
            type="button"
            className="ui-btn-primary"
            onClick={() => void disable()}
            disabled={busy || disableCode.length < 6}
          >
            {busy ? "Disabling…" : "Disable 2FA"}
          </button>
        </div>
      ) : enroll ? (
        <div className="space-y-4">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Scan this QR code with your authenticator app, then enter the 6-digit code to confirm.
          </p>
          <div
            className="inline-block rounded-lg border border-slate-200 p-3 dark:border-slate-300"
            style={{ backgroundColor: "#ffffff" }}
          >
            {enroll.qr_svg ? (
              <div
                className="h-[180px] w-[180px] [&_svg]:h-full [&_svg]:w-full [&_svg]:bg-white"
                style={{ backgroundColor: "#ffffff" }}
                dangerouslySetInnerHTML={{ __html: enroll.qr_svg }}
              />
            ) : (
              <p className="max-w-[12rem] text-xs text-slate-500">
                Scan unavailable. Enter the setup key below in your authenticator app.
              </p>
            )}
          </div>
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Manual setup key
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <code className="break-all rounded bg-slate-100 px-2 py-1 text-xs dark:bg-slate-800">
                {enroll.secret}
              </code>
              <button type="button" className="text-xs font-medium text-teal-700 hover:underline" onClick={() => void copySecret()}>
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
          </div>
          <div>
            <label htmlFor="totp-enroll-code" className="mb-1.5 block text-sm font-semibold text-slate-800 dark:text-slate-200">
              Confirmation code
            </label>
            <input
              id="totp-enroll-code"
              className="ui-field max-w-xs"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={8}
              placeholder="6-digit code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^\d]/g, "").slice(0, 6))}
              disabled={busy}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="ui-btn-primary"
              onClick={() => void confirmEnroll()}
              disabled={busy || code.length < 6}
            >
              {busy ? "Confirming…" : "Confirm and enable"}
            </button>
            <button
              type="button"
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300"
              onClick={() => {
                setEnroll(null);
                setCode("");
              }}
              disabled={busy}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Add an extra sign-in step with an authenticator app. 2FA is optional.
          </p>
          <button type="button" className="ui-btn-primary" onClick={() => void startEnroll()} disabled={busy || loading}>
            {busy ? "Starting…" : loading ? "Checking…" : "Enable 2FA"}
          </button>
        </div>
      )}
    </div>
  );
}
